import type { knownMcpServerMetadataRegistries } from "schemas/agentTools/mcpServers";
import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";
import type { OpencodeToolDisplay } from "../shared";

import {
  getQuotedArrayDetail,
  getToolDisplayText,
  getToolInputString,
  quotedDetail,
} from "../shared";
import {
  getAssetFindDetail,
  getAssetJobStatusDetail,
  getDownloadAssetsDetail,
  getUploadAssetsDetail,
} from "./bedrock-assets";
import {
  getDatabaseCollectionsDetail,
  getDatabaseDetail,
  getDatabaseRecordDetail,
  getDatabaseRecordsDetail,
} from "./bedrock-database";
import { getCustomDomainDetail } from "./bedrock-domains";
import {
  getMediaGenerationDisplay,
  getMediaGenerationStatusDisplay,
} from "./bedrock-media";
import { getMembershipsDetail } from "./bedrock-memberships";
import { getOrderEventsDetail, getOrdersFindDetail } from "./bedrock-orders";
import { getAnalyticsQueryDetail } from "./bedrock-query";
import {
  getRegistryCreateItemDetail,
  getRegistryFindItemsDetail,
  getRegistryGetItemDetail,
} from "./bedrock-registry";
import {
  getInputDetailPart,
  getToolPartOutputRecord,
  naturalDetail,
} from "./bedrock-shared";
import { getSitesListDetail } from "./bedrock-sites";
import {
  getDeletedTaskDetail,
  getTaskInputDetail,
  getTaskListDetail,
} from "./bedrock-tasks";

type KnownBedrockMcpToolName = Extract<
  keyof (typeof knownMcpServerMetadataRegistries)["bedrock"],
  string
>;

export function getBedrockMcpServerToolDisplay({
  toolName,
  toolPart,
  toolParts,
}: {
  toolName: KnownBedrockMcpToolName;
  toolPart: OpencodeToolPart;
  toolParts: readonly OpencodeToolPart[];
}) {
  const displayByToolName = {
    generate_image: () => {
      return getMediaGenerationDisplay({
        kind: "image",
        toolPart,
      });
    },
    generate_video: () => {
      return getMediaGenerationDisplay({
        kind: "video",
        toolPart,
      });
    },
    remove_image_background: () => ({
      summary: getToolDisplayText({
        completed: "Removed image background",
        pending: "Removing image background",
        running: "Removing image background",
      }),
      detail: null,
    }),
    get_generation_status: () => {
      return getMediaGenerationStatusDisplay({ toolPart, toolParts });
    },
    generate_design_directions: () => ({
      summary: getToolDisplayText({
        completed: "Generated design directions",
        pending: "Generating design directions",
        running: "Generating design directions",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["pageType"], toolPart }),
      }),
    }),
    database_create_collection: () => ({
      summary: getToolDisplayText({
        completed: "Created data collection",
        pending: "Creating data collection",
        running: "Creating data collection",
      }),
      detail: getDatabaseDetail({
        collectionFormat: "named",
        parts: [
          { label: "collection", properties: ["displayName", "collectionKey"] },
        ],
        toolPart,
      }),
    }),
    database_create_collection_field: () => ({
      summary: getToolDisplayText({
        completed: "Added data field",
        pending: "Adding data field",
        running: "Adding data field",
      }),
      detail: getDatabaseDetail({
        parts: [
          { label: "collection", properties: ["collectionKey"] },
          { label: "field", properties: ["displayName", "key"] },
        ],
        toolPart,
      }),
    }),
    database_create_record: () => ({
      summary: getToolDisplayText({
        completed: "Created data record",
        pending: "Creating data record",
        running: "Creating data record",
      }),
      detail: getDatabaseRecordDetail({ toolPart }),
    }),
    database_delete_collection_field: () => ({
      summary: getToolDisplayText({
        completed: "Deleted data field",
        pending: "Deleting data field",
        running: "Deleting data field",
      }),
      detail: getDatabaseDetail({
        parts: [
          { label: "collection", properties: ["collectionKey"] },
          { label: "field", properties: ["key"] },
        ],
        toolPart,
      }),
    }),
    database_delete_record: () => ({
      summary: getToolDisplayText({
        completed: "Deleted data record",
        pending: "Deleting data record",
        running: "Deleting data record",
      }),
      detail: getDatabaseRecordDetail({ toolPart }),
    }),
    database_get_collections: () => ({
      summary: getToolDisplayText({
        completed: "Listed data collections",
        pending: "Listing data collections",
        running: "Listing data collections",
      }),
      detail: getDatabaseCollectionsDetail({ toolPart }),
    }),
    database_query_records: () => ({
      summary: getToolDisplayText({
        completed: "Searched data records",
        pending: "Searching data records",
        running: "Searching data records",
      }),
      detail: getDatabaseRecordsDetail({ toolPart }),
    }),
    database_read_record: () => ({
      summary: getToolDisplayText({
        completed: "Read data record",
        pending: "Reading data record",
        running: "Reading data record",
      }),
      detail: getDatabaseRecordDetail({ toolPart }),
    }),
    database_update_collection: () => ({
      summary: getToolDisplayText({
        completed: "Updated data collection",
        pending: "Updating data collection",
        running: "Updating data collection",
      }),
      detail: getDatabaseDetail({
        collectionFormat: "named",
        parts: [
          {
            label: "collection",
            properties: ["displayName", "newCollectionKey", "collectionKey"],
          },
        ],
        toolPart,
      }),
    }),
    database_update_collection_field: () => ({
      summary: getToolDisplayText({
        completed: "Updated data field",
        pending: "Updating data field",
        running: "Updating data field",
      }),
      detail: getDatabaseDetail({
        parts: [
          { label: "collection", properties: ["collectionKey"] },
          { label: "field", properties: ["displayName", "newKey", "key"] },
        ],
        toolPart,
      }),
    }),
    database_update_record: () => ({
      summary: getToolDisplayText({
        completed: "Updated data record",
        pending: "Updating data record",
        running: "Updating data record",
      }),
      detail: getDatabaseRecordDetail({ toolPart }),
    }),
    create_task: () => ({
      summary: getToolDisplayText({
        completed: "Scheduled task",
        pending: "Scheduling task",
        running: "Scheduling task",
      }),
      detail: getTaskInputDetail({ toolPart }),
    }),
    list_tasks: () => ({
      summary: getToolDisplayText({
        completed: "Listed tasks",
        pending: "Listing tasks",
        running: "Listing tasks",
      }),
      detail: getTaskListDetail({ toolPart }),
    }),
    update_task: () => ({
      summary: getToolDisplayText({
        completed: "Updated task",
        pending: "Updating task",
        running: "Updating task",
      }),
      detail: getTaskInputDetail({ toolPart }),
    }),
    delete_task: () => ({
      summary: getToolDisplayText({
        completed: "Deleted task",
        pending: "Deleting task",
        running: "Deleting task",
      }),
      detail: getDeletedTaskDetail({ toolPart, toolParts }),
    }),
    submit_feature_request: () => ({
      summary: getToolDisplayText({
        completed: "Sent feature request to the Replo team",
        pending: "Sending feature request",
        running: "Sending feature request",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["title"], toolPart }),
      }),
    }),
    report_bug: () => ({
      summary: getToolDisplayText({
        completed: "Reported bug to the Replo team",
        pending: "Reporting bug",
        running: "Reporting bug",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["title"], toolPart }),
      }),
    }),
    list_v1_pages: () => ({
      summary: getToolDisplayText({
        completed: "Listed your v1 pages",
        pending: "Listing your v1 pages",
        running: "Listing your v1 pages",
      }),
      detail: null,
    }),
    migrate_v1_page: () => {
      const route = getToolPartOutputRecord({ toolPart })?.route;
      return {
        summary: getToolDisplayText({
          completed: "Migrated a v1 page",
          pending: "Migrating a v1 page",
          running: "Migrating a v1 page",
        }),
        detail: quotedDetail({
          value: typeof route === "string" ? route : null,
        }),
      };
    },
    get_v1_migration: () => {
      const route = getToolPartOutputRecord({ toolPart })?.route;
      return {
        summary: getToolDisplayText({
          completed: "Checked a v1 page migration",
          pending: "Checking a v1 page migration",
          running: "Checking a v1 page migration",
        }),
        detail: quotedDetail({
          value: typeof route === "string" ? route : null,
        }),
      };
    },
    replo_orders_find: () => ({
      summary: getToolDisplayText({
        completed: "Searched orders",
        pending: "Searching orders",
        running: "Searching orders",
      }),
      detail: getOrdersFindDetail({ toolPart }),
    }),
    replo_orders_get: () => ({
      summary: getToolDisplayText({
        completed: "Loaded order",
        pending: "Loading order",
        running: "Loading order",
      }),
      detail: null,
    }),
    replo_orders_events_list: () => ({
      summary: getToolDisplayText({
        completed: "Listed order events",
        pending: "Listing order events",
        running: "Listing order events",
      }),
      detail: getOrderEventsDetail({ toolPart }),
    }),
    replo_orders_notes_add: () => ({
      summary: getToolDisplayText({
        completed: "Added order note",
        pending: "Adding order note",
        running: "Adding order note",
      }),
      detail: null,
    }),
    replo_orders_notes_edit: () => ({
      summary: getToolDisplayText({
        completed: "Edited order note",
        pending: "Editing order note",
        running: "Editing order note",
      }),
      detail: null,
    }),
    replo_orders_notes_delete: () => ({
      summary: getToolDisplayText({
        completed: "Deleted order note",
        pending: "Deleting order note",
        running: "Deleting order note",
      }),
      detail: null,
    }),
    replo_orders_mark_fulfilled: () => ({
      summary: getToolDisplayText({
        completed: "Marked order fulfilled",
        pending: "Marking order fulfilled",
        running: "Marking order fulfilled",
      }),
      detail: null,
    }),
    replo_orders_refund: () => ({
      summary: getToolDisplayText({
        completed: "Refunded order",
        pending: "Refunding order",
        running: "Refunding order",
      }),
      detail: quotedDetail({
        value: getToolInputString({
          properties: ["amount", "reason"],
          toolPart,
        }),
      }),
    }),
    replo_fulfillment_orders_cancel: () => ({
      summary: getToolDisplayText({
        completed: "Cancelled fulfillment order",
        pending: "Cancelling fulfillment order",
        running: "Cancelling fulfillment order",
      }),
      detail: null,
    }),
    list_project_memberships: () => ({
      summary: getToolDisplayText({
        completed: "Listed project memberships",
        pending: "Listing project memberships",
        running: "Listing project memberships",
      }),
      detail: getMembershipsDetail({
        emptyLabel: "with no project members",
        toolPart,
      }),
    }),
    add_project_membership: () => ({
      summary: getToolDisplayText({
        completed: "Added project memberships",
        pending: "Adding project memberships",
        running: "Adding project memberships",
      }),
      detail: getQuotedArrayDetail({ value: toolPart.state.input.emails }),
    }),
    remove_project_membership: () => ({
      summary: getToolDisplayText({
        completed: "Removed project membership",
        pending: "Removing project membership",
        running: "Removing project membership",
      }),
      detail: getMembershipsDetail({
        emptyLabel: "with no project members remaining",
        toolPart,
      }),
    }),
    list_workspace_memberships: () => ({
      summary: getToolDisplayText({
        completed: "Listed workspace memberships",
        pending: "Listing workspace memberships",
        running: "Listing workspace memberships",
      }),
      detail: getMembershipsDetail({
        emptyLabel: "with no workspace members",
        includeRole: true,
        toolPart,
      }),
    }),
    add_workspace_membership: () => ({
      summary: getToolDisplayText({
        completed: "Added workspace memberships",
        pending: "Adding workspace memberships",
        running: "Adding workspace memberships",
      }),
      detail: getQuotedArrayDetail({ value: toolPart.state.input.emails }),
    }),
    remove_workspace_membership: () => ({
      summary: getToolDisplayText({
        completed: "Removed workspace membership",
        pending: "Removing workspace membership",
        running: "Removing workspace membership",
      }),
      detail: getMembershipsDetail({
        emptyLabel: "with no workspace members remaining",
        includeRole: true,
        toolPart,
      }),
    }),
    create_registry_item: () => ({
      summary: getToolDisplayText({
        completed: "Created registry item",
        pending: "Creating registry item",
        running: "Creating registry item",
      }),
      detail: getRegistryCreateItemDetail({ toolPart }),
    }),
    find_assets: () => {
      const query = getToolInputString({ properties: ["q"], toolPart });
      return {
        summary: !query
          ? getToolDisplayText({
              completed: "Listed Replo assets",
              pending: "Listing Replo assets",
              running: "Listing Replo assets",
            })
          : getToolDisplayText({
              completed: `Searched Replo assets "${query}"`,
              pending: `Searching Replo assets "${query}"`,
              running: `Searching Replo assets "${query}"`,
            }),
        detail: getAssetFindDetail({ toolPart }),
      };
    },
    list_integrations: () => ({
      summary: getToolDisplayText({
        completed: "Checked integrations",
        pending: "Checking integrations",
        running: "Checking integrations",
      }),
      detail: naturalDetail({
        parts: [
          getInputDetailPart({
            label: "tagged",
            properties: ["tag"],
            toolPart,
          }),
        ],
      }),
    }),
    messages_react: () => ({
      summary: getToolDisplayText({
        completed:
          getToolPartOutputRecord({ toolPart })?.reacted === true
            ? "Requested a reaction"
            : "Skipped reaction",
        pending: "Reacting to a message",
        running: "Reacting to a message",
      }),
      detail: quotedDetail({
        value: getToolInputString({ toolPart, properties: ["emoji"] }),
      }),
    }),
    messages_send: () => ({
      summary: getToolDisplayText({
        completed: "Sent a message",
        pending: "Sending a message",
        running: "Sending a message",
      }),
      detail: null,
    }),
    query_replo_analytics: () => ({
      summary: getToolDisplayText({
        completed: "Queried Replo analytics",
        pending: "Querying Replo analytics",
        running: "Querying Replo analytics",
      }),
      detail: getAnalyticsQueryDetail({ toolPart }),
    }),
    sightglass_find_ads: () => ({
      summary: getToolDisplayText({
        completed: "Searched ads",
        pending: "Searching ads",
        running: "Searching ads",
      }),
      detail: quotedDetail({
        value: getToolInputString({
          properties: ["search", "companyIds", "pageTypes"],
          toolPart,
        }),
      }),
    }),
    sightglass_get_ad: () => ({
      summary: getToolDisplayText({
        completed: "Read an ad",
        pending: "Reading an ad",
        running: "Reading an ad",
      }),
      detail: null,
    }),
    sightglass_find_site_pages: () => ({
      summary: getToolDisplayText({
        completed: "Searched landing pages",
        pending: "Searching landing pages",
        running: "Searching landing pages",
      }),
      detail: quotedDetail({
        value: getToolInputString({
          properties: ["search", "domains", "pageTypes"],
          toolPart,
        }),
      }),
    }),
    sightglass_get_site_page: () => ({
      summary: getToolDisplayText({
        completed: "Read a landing page",
        pending: "Reading a landing page",
        running: "Reading a landing page",
      }),
      detail: null,
    }),
    registry_find_items: () => ({
      summary: getToolDisplayText({
        completed: "Found registry items",
        pending: "Finding registry items",
        running: "Finding registry items",
      }),
      detail:
        getRegistryFindItemsDetail({ toolPart }) ??
        quotedDetail({
          value: getToolInputString({
            properties: ["type", "search", "category"],
            toolPart,
          }),
        }),
    }),
    registry_get_item: () => ({
      summary: getToolDisplayText({
        completed: "Got registry item",
        pending: "Getting registry item",
        running: "Getting registry item",
      }),
      detail:
        getRegistryGetItemDetail({ toolPart }) ??
        quotedDetail({
          value: getToolInputString({ properties: ["type", "slug"], toolPart }),
        }),
    }),
    resolve_legacy_v2_product_ids: () => ({
      summary: getToolDisplayText({
        completed: "Matched product IDs",
        pending: "Matching product IDs",
        running: "Matching product IDs",
      }),
      detail: getQuotedArrayDetail({ value: toolPart.state.input.productIds }),
    }),
    scrape_page: () => ({
      summary: getToolDisplayText({
        completed: "Scraped page",
        pending: "Scraping page",
        running: "Scraping page",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["url"], toolPart }),
      }),
    }),
    show_integration_status: () => ({
      summary: getToolDisplayText({
        completed: "Checked integration status",
        pending: "Checking integration status",
        running: "Checking integration status",
      }),
      detail: naturalDetail({
        parts: [
          getInputDetailPart({
            label: "for integration",
            properties: ["integrationKey"],
            toolPart,
          }),
        ],
      }),
    }),
    list_sites: () => ({
      summary: getToolDisplayText({
        completed: "Listed sites",
        pending: "Listing sites",
        running: "Listing sites",
      }),
      detail:
        getSitesListDetail({ toolPart }) ??
        quotedDetail({
          value: getToolInputString({ properties: ["directory"], toolPart }),
        }),
    }),
    create_site: () => ({
      summary: getToolDisplayText({
        completed: "Created site",
        pending: "Creating site",
        running: "Creating site",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["directory"], toolPart }),
      }),
    }),
    provision_site: () => ({
      summary: getToolDisplayText({
        completed: "Set up site",
        pending: "Setting up site",
        running: "Setting up site",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["directory"], toolPart }),
      }),
    }),
    content_read_entry: () => ({
      summary: getToolDisplayText({
        completed: "Read content entry",
        pending: "Reading content entry",
        running: "Reading content entry",
      }),
      detail: quotedDetail({
        value: getToolInputString({
          properties: ["entrySlug"],
          toolPart,
        }),
      }),
    }),
    content_create_entry: () => ({
      summary: getToolDisplayText({
        completed: "Created content entry",
        pending: "Creating content entry",
        running: "Creating content entry",
      }),
      detail: quotedDetail({
        value: getToolInputString({
          properties: ["entrySlug"],
          toolPart,
        }),
      }),
    }),
    content_update_entry: () => ({
      summary: getToolDisplayText({
        completed: "Updated content entry",
        pending: "Updating content entry",
        running: "Updating content entry",
      }),
      detail: quotedDetail({
        value: getToolInputString({
          properties: ["entrySlug"],
          toolPart,
        }),
      }),
    }),
    content_delete_entry: () => ({
      summary: getToolDisplayText({
        completed: "Deleted content entry",
        pending: "Deleting content entry",
        running: "Deleting content entry",
      }),
      detail: quotedDetail({
        value: getToolInputString({
          properties: ["entrySlug"],
          toolPart,
        }),
      }),
    }),
    delete_site: () => ({
      summary: getToolDisplayText({
        completed: "Deleted site",
        pending: "Deleting site",
        running: "Deleting site",
      }),
      detail: null,
    }),
    upload_assets: () => ({
      summary: getToolDisplayText({
        completed: "Uploaded Replo assets",
        pending: "Uploading Replo assets",
        running: "Uploading Replo assets",
      }),
      detail: getUploadAssetsDetail({ toolPart }),
    }),
    download_assets: () => ({
      summary: getToolDisplayText({
        completed: "Downloaded Replo assets",
        pending: "Downloading Replo assets",
        running: "Downloading Replo assets",
      }),
      detail: getDownloadAssetsDetail({ toolPart }),
    }),
    get_asset_download_status: () => ({
      summary: getToolDisplayText({
        completed: "Checked Replo asset downloads",
        pending: "Checking Replo asset downloads",
        running: "Checking Replo asset downloads",
      }),
      detail: getAssetJobStatusDetail({
        sourceToolName: "download_assets",
        toolPart,
        toolParts,
      }),
    }),
    get_asset_upload_status: () => ({
      summary: getToolDisplayText({
        completed: "Checked Replo asset uploads",
        pending: "Checking Replo asset uploads",
        running: "Checking Replo asset uploads",
      }),
      detail: getAssetJobStatusDetail({
        sourceToolName: "upload_assets",
        toolPart,
        toolParts,
      }),
    }),
    add_custom_domain: () => ({
      summary: getToolDisplayText({
        completed: "Added custom domain",
        pending: "Adding custom domain",
        running: "Adding custom domain",
      }),
      detail: getCustomDomainDetail({ toolPart }),
    }),
    find_custom_domains: () => ({
      summary: getToolDisplayText({
        completed: "Listed custom domains",
        pending: "Listing custom domains",
        running: "Listing custom domains",
      }),
      detail: getCustomDomainDetail({ toolPart }),
    }),
    get_custom_domain: () => ({
      summary: getToolDisplayText({
        completed: "Retrieved custom domain",
        pending: "Retrieving custom domain",
        running: "Retrieving custom domain",
      }),
      detail: getCustomDomainDetail({ toolPart }),
    }),
    delete_custom_domain: () => ({
      summary: getToolDisplayText({
        completed: "Deleted custom domain",
        pending: "Deleting custom domain",
        running: "Deleting custom domain",
      }),
      detail: null,
    }),
    publish_email_template: () => ({
      summary: getToolDisplayText({
        completed: "Published email",
        pending: "Publishing email",
        running: "Publishing email",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["type"], toolPart }),
      }),
    }),
    verify_custom_domain: () => ({
      summary: getToolDisplayText({
        completed: "Verified custom domain",
        pending: "Verifying custom domain",
        running: "Verifying custom domain",
      }),
      detail: getCustomDomainDetail({ toolPart }),
    }),
    seo_keywords_overview: () => ({
      summary: getToolDisplayText({
        completed: "Checked keyword metrics",
        pending: "Checking keyword metrics",
        running: "Checking keyword metrics",
      }),
      detail: getQuotedArrayDetail({ value: toolPart.state.input.keywords }),
    }),
    seo_keyword_suggestions: () => ({
      summary: getToolDisplayText({
        completed: "Found keyword suggestions",
        pending: "Finding keyword suggestions",
        running: "Finding keyword suggestions",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["keyword"], toolPart }),
      }),
    }),
    seo_keyword_difficulty: () => ({
      summary: getToolDisplayText({
        completed: "Checked keyword difficulty",
        pending: "Checking keyword difficulty",
        running: "Checking keyword difficulty",
      }),
      detail: getQuotedArrayDetail({ value: toolPart.state.input.keywords }),
    }),
    seo_domain_overview: () => ({
      summary: getToolDisplayText({
        completed: "Checked domain visibility",
        pending: "Checking domain visibility",
        running: "Checking domain visibility",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["target"], toolPart }),
      }),
    }),
    seo_domain_ranked_keywords: () => ({
      summary: getToolDisplayText({
        completed: "Listed ranked keywords",
        pending: "Listing ranked keywords",
        running: "Listing ranked keywords",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["target"], toolPart }),
      }),
    }),
    seo_domain_competitors: () => ({
      summary: getToolDisplayText({
        completed: "Found competitor domains",
        pending: "Finding competitor domains",
        running: "Finding competitor domains",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["target"], toolPart }),
      }),
    }),
    seo_domain_traffic: () => ({
      summary: getToolDisplayText({
        completed: "Estimated domain traffic",
        pending: "Estimating domain traffic",
        running: "Estimating domain traffic",
      }),
      detail: getQuotedArrayDetail({ value: toolPart.state.input.targets }),
    }),
    seo_domain_intersection: () => ({
      summary: getToolDisplayText({
        completed: "Compared domain keywords",
        pending: "Comparing domain keywords",
        running: "Comparing domain keywords",
      }),
      detail: quotedDetail({
        value: getToolInputString({
          properties: ["target1", "target2"],
          toolPart,
        }),
      }),
    }),
    seo_serp_search: () => ({
      summary: getToolDisplayText({
        completed: "Fetched Google results",
        pending: "Fetching Google results",
        running: "Fetching Google results",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["keyword"], toolPart }),
      }),
    }),
    seo_keywords_search_volume: () => ({
      summary: getToolDisplayText({
        completed: "Checked search volumes",
        pending: "Checking search volumes",
        running: "Checking search volumes",
      }),
      detail: getQuotedArrayDetail({ value: toolPart.state.input.keywords }),
    }),
    seo_keywords_for_site: () => ({
      summary: getToolDisplayText({
        completed: "Found site keywords",
        pending: "Finding site keywords",
        running: "Finding site keywords",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["target"], toolPart }),
      }),
    }),
    seo_domain_technologies: () => ({
      summary: getToolDisplayText({
        completed: "Identified site technologies",
        pending: "Identifying site technologies",
        running: "Identifying site technologies",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["target"], toolPart }),
      }),
    }),
    seo_domain_whois: () => ({
      summary: getToolDisplayText({
        completed: "Checked Whois data",
        pending: "Checking Whois data",
        running: "Checking Whois data",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["domain"], toolPart }),
      }),
    }),
    seo_backlinks_summary: () => ({
      summary: getToolDisplayText({
        completed: "Checked backlink profile",
        pending: "Checking backlink profile",
        running: "Checking backlink profile",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["target"], toolPart }),
      }),
    }),
    seo_backlinks_list: () => ({
      summary: getToolDisplayText({
        completed: "Listed backlinks",
        pending: "Listing backlinks",
        running: "Listing backlinks",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["target"], toolPart }),
      }),
    }),
    seo_referring_domains: () => ({
      summary: getToolDisplayText({
        completed: "Listed referring domains",
        pending: "Listing referring domains",
        running: "Listing referring domains",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["target"], toolPart }),
      }),
    }),
    seo_backlinks_bulk_metrics: () => ({
      summary: getToolDisplayText({
        completed: "Compared backlink counts",
        pending: "Comparing backlink counts",
        running: "Comparing backlink counts",
      }),
      detail: getQuotedArrayDetail({ value: toolPart.state.input.targets }),
    }),
    seo_llm_mentions: () => ({
      summary: getToolDisplayText({
        completed: "Searched LLM mentions",
        pending: "Searching LLM mentions",
        running: "Searching LLM mentions",
      }),
      detail: null,
    }),
    seo_llm_mentions_overview: () => ({
      summary: getToolDisplayText({
        completed: "Checked LLM mention metrics",
        pending: "Checking LLM mention metrics",
        running: "Checking LLM mention metrics",
      }),
      detail: null,
    }),
    seo_ai_keyword_volume: () => ({
      summary: getToolDisplayText({
        completed: "Checked AI search volumes",
        pending: "Checking AI search volumes",
        running: "Checking AI search volumes",
      }),
      detail: getQuotedArrayDetail({ value: toolPart.state.input.keywords }),
    }),
    seo_page_audit: () => ({
      summary: getToolDisplayText({
        completed: "Audited page",
        pending: "Auditing page",
        running: "Auditing page",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["url"], toolPart }),
      }),
    }),
    seo_site_audit_start: () => ({
      summary: getToolDisplayText({
        completed: "Started site audit",
        pending: "Starting site audit",
        running: "Starting site audit",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["target"], toolPart }),
      }),
    }),
    seo_site_audit_results: () => ({
      summary: getToolDisplayText({
        completed: "Checked site audit",
        pending: "Checking site audit",
        running: "Checking site audit",
      }),
      detail: null,
    }),
    seo_content_citations: () => ({
      summary: getToolDisplayText({
        completed: "Found content citations",
        pending: "Finding content citations",
        running: "Finding content citations",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["keyword"], toolPart }),
      }),
    }),
    seo_content_sentiment: () => ({
      summary: getToolDisplayText({
        completed: "Checked brand sentiment",
        pending: "Checking brand sentiment",
        running: "Checking brand sentiment",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["keyword"], toolPart }),
      }),
    }),
    seo_business_listings: () => ({
      summary: getToolDisplayText({
        completed: "Searched business listings",
        pending: "Searching business listings",
        running: "Searching business listings",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["name"], toolPart }),
      }),
    }),
    seo_reviews_start: () => ({
      summary: getToolDisplayText({
        completed: "Started review collection",
        pending: "Starting review collection",
        running: "Starting review collection",
      }),
      detail: quotedDetail({
        value: getToolInputString({
          properties: ["platform", "target"],
          toolPart,
        }),
      }),
    }),
    seo_reviews_result: () => ({
      summary: getToolDisplayText({
        completed: "Checked collected reviews",
        pending: "Checking collected reviews",
        running: "Checking collected reviews",
      }),
      detail: null,
    }),
    seo_merchant_products_start: () => ({
      summary: getToolDisplayText({
        completed: "Started product search",
        pending: "Starting product search",
        running: "Starting product search",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["keyword"], toolPart }),
      }),
    }),
    seo_merchant_products_result: () => ({
      summary: getToolDisplayText({
        completed: "Checked product results",
        pending: "Checking product results",
        running: "Checking product results",
      }),
      detail: null,
    }),
    get_account: () => ({
      summary: getToolDisplayText({
        completed: "Read your account",
        pending: "Reading your account",
        running: "Reading your account",
      }),
      detail: null,
    }),
    get_checkout_payments: () => ({
      summary: getToolDisplayText({
        completed: "Read checkout payments",
        pending: "Reading checkout payments",
        running: "Reading checkout payments",
      }),
      detail: null,
    }),
    get_project: () => ({
      summary: getToolDisplayText({
        completed: "Read the project",
        pending: "Reading the project",
        running: "Reading the project",
      }),
      detail: null,
    }),
    set_up_checkout_payments: () => ({
      summary: getToolDisplayText({
        completed: "Set up checkout payments",
        pending: "Setting up checkout payments",
        running: "Setting up checkout payments",
      }),
      detail: null,
    }),
    get_workspace: () => ({
      summary: getToolDisplayText({
        completed: "Read the workspace",
        pending: "Reading the workspace",
        running: "Reading the workspace",
      }),
      detail: null,
    }),
    update_account: () => ({
      summary: getToolDisplayText({
        completed: "Updated your account",
        pending: "Updating your account",
        running: "Updating your account",
      }),
      detail: null,
    }),
    update_project: () => ({
      summary: getToolDisplayText({
        completed: "Updated the project",
        pending: "Updating the project",
        running: "Updating the project",
      }),
      detail: null,
    }),
    update_workspace: () => ({
      summary: getToolDisplayText({
        completed: "Updated the workspace",
        pending: "Updating the workspace",
        running: "Updating the workspace",
      }),
      detail: null,
    }),
    pagespeed_analyze: () => ({
      summary: getToolDisplayText({
        completed: "Measured page speed",
        pending: "Measuring page speed",
        running: "Measuring page speed",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["url"], toolPart }),
      }),
    }),
    find_chat_sessions: () => ({
      summary: getToolDisplayText({
        completed: "Searched your chats",
        pending: "Searching your chats",
        running: "Searching your chats",
      }),
      detail: naturalDetail({
        parts: [
          getInputDetailPart({
            label: "for",
            properties: ["contains"],
            toolPart,
          }),
        ],
      }),
    }),
    get_chat_session: () => {
      const title = getToolPartOutputRecord({ toolPart })?.title;
      return {
        summary: getToolDisplayText({
          completed: "Read an earlier chat",
          pending: "Reading an earlier chat",
          running: "Reading an earlier chat",
        }),
        detail: quotedDetail({
          value: typeof title === "string" ? title : null,
        }),
      };
    },
    research_find_source: () => ({
      summary: getToolDisplayText({
        completed: "Found research sources",
        pending: "Finding research sources",
        running: "Finding research sources",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["question"], toolPart }),
      }),
    }),
    research_describe_source: () => ({
      summary: getToolDisplayText({
        completed: "Read research source inputs",
        pending: "Reading research source inputs",
        running: "Reading research source inputs",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["sourceId"], toolPart }),
      }),
    }),
    research_run_source: () => ({
      summary: getToolDisplayText({
        completed: "Collected research records",
        pending: "Collecting research records",
        running: "Collecting research records",
      }),
      detail: quotedDetail({
        value: getToolInputString({ properties: ["sourceId"], toolPart }),
      }),
    }),
    research_get_job: () => ({
      summary: getToolDisplayText({
        completed: "Read research job",
        pending: "Reading research job",
        running: "Reading research job",
      }),
      detail: null,
    }),
  } satisfies Record<KnownBedrockMcpToolName, () => OpencodeToolDisplay>;

  return displayByToolName[toolName]();
}
